// /piano-drills — chord & rhythm practice playground. Two surfaces:
//
// "Rhythm drill": pick a key, build a diatonic progression (chips, presets,
// or a typed "1 5 6 4"), choose a rhythm + inversion mode, and either follow
// along (guide mode plays it and lights the keys) or get judged (score mode
// listens to what you play).
//
// "Stay in the pocket": untimed voice-leading practice — the page calls out
// plain chord names and you voice each one however you like, as long as every
// note stays inside the lit G3–G4 window (POCKET_WINDOW). Judged per chord
// with movement coaching from pocketFeedback.
//
// Input comes from a MIDI keyboard, the on-screen keys, or the A–K row; the
// pocket surface also supports tap-to-select (click keys to accumulate a
// chord, like the circle-of-fifths drills).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  buildDrill,
  chordLabel,
  checkPocketChord,
  clampRange,
  DEFAULT_RANGE,
  KB_KEYS,
  kbAnchor,
  parseProgression,
  POCKET_WINDOW,
  pocketFeedback,
  slashLabel,
  soundingEventIndices,
} from "../lib/pianoDrills";
import { parsePattern, patternEvents } from "../lib/chordPattern";
import { ensureAudio, playTone } from "../lib/pianoDrillsAudio";
import { useMidiInput } from "../components/synthlab/useSynthInput";
import DrillControls from "../components/pianoDrills/DrillControls";
import SheetView from "../components/pianoDrills/SheetView";
import PianoRoll from "../components/pianoDrills/PianoRoll";
import { useDrillTransport } from "../components/pianoDrills/useDrillTransport";
import { cn } from "../lib/utils";

const EMPTY_SET = new Set();

const SURFACES = [
  { id: "rhythm", label: "Rhythm drill" },
  { id: "pocket", label: "Stay in the pocket" },
];

const INVERSION_NAMES = ["root position", "1st inversion", "2nd inversion"];

// One note per pitch class (the lowest), sorted — the accepted reduction the
// pocket drill hands to the next chord's pocketFeedback as prevMidis.
const reduceToPcs = (midis) => {
  const byPc = new Map();
  [...midis]
    .sort((a, b) => a - b)
    .forEach((m) => {
      const pc = ((m % 12) + 12) % 12;
      if (!byPc.has(pc)) byPc.set(pc, m);
    });
  return [...byPc.values()].sort((a, b) => a - b);
};

const ScoreBar = ({ score, playing }) => {
  const judged = score.hits + score.misses;
  const accuracy = judged > 0 ? Math.round((100 * score.hits) / judged) : null;
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-xl border border-slate-200 bg-white/80 px-4 py-2 text-sm">
      <span className="font-semibold text-slate-700">Score mode</span>
      <span className="text-emerald-700">Hits: {score.hits}</span>
      <span className="text-rose-600">Misses: {score.misses}</span>
      <span className="text-amber-600">Wrong notes: {score.extras}</span>
      <span className="font-medium text-slate-700">
        Accuracy: {accuracy == null ? "—" : `${accuracy}%`}
      </span>
      {!playing && (
        <span className="text-slate-400">press play, then play the chords in time</span>
      )}
    </div>
  );
};

export default function PianoDrills() {
  const [surface, setSurface] = useState("rhythm");
  const [keyName, setKeyName] = useState("C");
  const [progression, setProgression] = useState([1, 5, 6, 4]);
  const [progressionText, setProgressionText] = useState("");
  const [rhythmId, setRhythmId] = useState("straight");
  const [patternText, setPatternText] = useState("[35] 1");
  const [voicingId, setVoicingId] = useState("close");
  const [inversionModeId, setInversionModeId] = useState("roots");
  const [bassId, setBassId] = useState("off");
  const [low, setLow] = useState(DEFAULT_RANGE.low);
  const [high, setHigh] = useState(DEFAULT_RANGE.high);
  const [bpm, setBpm] = useState(80);
  const [mode, setMode] = useState("guide");
  const [metronome, setMetronome] = useState(true);
  const [activeMidis, setActiveMidis] = useState(EMPTY_SET);

  // Pocket surface: position in the progression, lap count, tap-picked
  // notes, the previous accepted chord (reduced), and per-chord feedback:
  // null | { type: "ok", label, coach } | { type: "window" } | { type: "wrong" }.
  const [pocketIdx, setPocketIdx] = useState(0);
  const [pocketLap, setPocketLap] = useState(1);
  const [pocketPrev, setPocketPrev] = useState(null);
  const [pocketStatus, setPocketStatus] = useState(null);
  const [pickedMidis, setPickedMidis] = useState(EMPTY_SET);

  const timeoutsRef = useRef([]);
  const later = useCallback((fn, ms) => {
    timeoutsRef.current.push(setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timeoutsRef.current.forEach(clearTimeout), []);

  const parsedPattern = useMemo(
    () => (rhythmId === "custom" ? parsePattern(patternText) : null),
    [rhythmId, patternText]
  );

  // Typed progression: when the box has parseable text it wins over the chip
  // builder; empty text hands control back to the chips (DrillControls also
  // clears the text on any chip interaction, and presets fill both).
  const typedProg = useMemo(() => {
    const text = progressionText.trim();
    return text ? parseProgression(text) : null;
  }, [progressionText]);
  const typedDegrees = typedProg && !typedProg.error ? typedProg.degrees : null;

  const drill = useMemo(() => {
    const customEventsForBar =
      parsedPattern && !parsedPattern.error
        ? (degree, midis) => patternEvents(parsedPattern, keyName, degree, midis)
        : null;
    return buildDrill(
      keyName,
      typedDegrees || progression,
      rhythmId,
      low,
      high,
      bassId,
      customEventsForBar,
      voicingId,
      inversionModeId,
      typedDegrees
    );
  }, [
    keyName,
    progression,
    typedDegrees,
    rhythmId,
    low,
    high,
    bassId,
    parsedPattern,
    voicingId,
    inversionModeId,
  ]);

  const { playing, toggle, stop, currentAbsStep, score, statuses, registerNoteOn } =
    useDrillTransport({ drill, bpm, mode, metronome });

  // The pocket is untimed — leaving the rhythm surface silences the transport.
  useEffect(() => {
    if (surface === "pocket") stop();
  }, [surface, stop]);

  /* ---------- pocket judging ---------- */

  const pocketDegrees = useMemo(
    () => typedDegrees || progression,
    [typedDegrees, progression]
  );
  const pocketProgKey = pocketDegrees.join(",");

  const resetPocket = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setPocketIdx(0);
    setPocketLap(1);
    setPocketPrev(null);
    setPocketStatus(null);
    setPickedMidis(EMPTY_SET);
  }, []);

  // New key, new progression, or a surface flip → start the laps over.
  useEffect(() => {
    resetPocket();
  }, [surface, keyName, pocketProgKey, resetPocket]);

  const judgePocket = useCallback(
    (midis) => {
      const degs = pocketDegrees;
      if (!degs.length) return;
      // An accepted chord is advancing — don't re-judge until it does.
      if (pocketStatus && pocketStatus.type === "ok") return;
      const pcs = new Set(midis.map((m) => ((m % 12) + 12) % 12));
      if (pcs.size < 3) {
        if (pocketStatus) setPocketStatus(null);
        return;
      }
      const degree = degs[pocketIdx];
      const res = checkPocketChord(midis, keyName, degree, POCKET_WINDOW.low, POCKET_WINDOW.high);
      if (res.ok) {
        const fb = pocketFeedback(
          midis,
          pocketPrev,
          keyName,
          degree,
          POCKET_WINDOW.low,
          POCKET_WINDOW.high
        );
        const coach =
          fb.bestMoved == null
            ? null
            : fb.moved <= fb.bestMoved
            ? `only moved ${fb.moved} — smooth`
            : `you moved ${fb.moved}; ${fb.bestLabel} was ${fb.bestMoved} away`;
        setPocketStatus({
          type: "ok",
          label: `${slashLabel(keyName, degree, res.inversion)} — ${INVERSION_NAMES[res.inversion]}`,
          coach,
        });
        setPocketPrev(reduceToPcs(midis));
        later(() => {
          setPickedMidis(EMPTY_SET);
          setPocketStatus(null);
          if (pocketIdx + 1 >= degs.length) {
            setPocketLap((l) => l + 1);
            setPocketIdx(0);
          } else {
            setPocketIdx(pocketIdx + 1);
          }
        }, 1400);
      } else if (res.outOfWindow) {
        setPocketStatus({ type: "window" });
      } else {
        setPocketStatus({ type: "wrong" });
      }
    },
    [pocketDegrees, pocketIdx, pocketPrev, pocketStatus, keyName, later]
  );

  /* ---------- note input (MIDI, on-screen keys, qwerty) ---------- */

  const heldRef = useRef(new Set());
  // Live refs so press/pocketTap stay referentially stable while always
  // seeing the current surface/judge/picked state (same idiom as the
  // transport hook's drillRef).
  const surfaceRef = useRef(surface);
  surfaceRef.current = surface;
  const judgePocketRef = useRef(judgePocket);
  judgePocketRef.current = judgePocket;
  const pickedRef = useRef(pickedMidis);
  pickedRef.current = pickedMidis;

  const press = useCallback(
    (midi) => {
      ensureAudio();
      playTone(midi, { gain: 0.12 });
      heldRef.current.add(midi);
      setActiveMidis(new Set(heldRef.current));
      if (surfaceRef.current === "pocket") {
        judgePocketRef.current([...new Set([...heldRef.current, ...pickedRef.current])]);
      } else {
        registerNoteOn(midi);
      }
    },
    [registerNoteOn]
  );

  const release = useCallback((midi) => {
    if (!heldRef.current.has(midi)) return;
    heldRef.current.delete(midi);
    setActiveMidis(new Set(heldRef.current));
  }, []);

  // Tap-to-select for the pocket surface: clicks toggle notes in pickedMidis
  // (they persist after pointer-up, mouse-friendly), judged together with any
  // held MIDI/qwerty notes.
  const pocketTap = useCallback((midi) => {
    ensureAudio();
    playTone(midi, { gain: 0.12 });
    const next = new Set(pickedRef.current);
    if (next.has(midi)) next.delete(midi);
    else next.add(midi);
    setPickedMidis(next);
    judgePocketRef.current([...new Set([...next, ...heldRef.current])]);
  }, []);

  const { status: midiStatus, inputs: midiInputs } = useMidiInput(press, release, true);

  // Computer-keyboard octave (A–K row) anchored to a C inside the range.
  const kbMap = useMemo(() => {
    const anchor = kbAnchor(low, high);
    const map = {};
    KB_KEYS.forEach(({ offset, key }) => {
      const midi = anchor + offset;
      if (midi >= low && midi <= high) map[key] = midi;
    });
    return map;
  }, [low, high]);

  const kbHints = useMemo(() => {
    const hints = {};
    Object.entries(kbMap).forEach(([key, midi]) => {
      hints[midi] = key;
    });
    return hints;
  }, [kbMap]);

  useEffect(() => {
    const down = (e) => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = document.activeElement && document.activeElement.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const midi = kbMap[e.key.toLowerCase()];
      if (midi != null) press(midi);
    };
    const up = (e) => {
      const midi = kbMap[e.key.toLowerCase()];
      if (midi != null) release(midi);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      Object.values(kbMap).forEach(release);
    };
  }, [kbMap, press, release]);

  /* ---------- derived display state ---------- */

  const currentEventIndices = useMemo(
    () => (playing ? soundingEventIndices(drill, currentAbsStep) : []),
    [drill, playing, currentAbsStep]
  );

  const targetMidis = useMemo(() => {
    if (!currentEventIndices.length) return EMPTY_SET;
    return new Set(
      currentEventIndices.flatMap((i) => (drill.events[i] ? drill.events[i].midis : []))
    );
  }, [drill, currentEventIndices]);

  // On the pocket surface tap-picked notes light up alongside held ones.
  const rollActiveMidis = useMemo(() => {
    if (surface !== "pocket" || pickedMidis.size === 0) return activeMidis;
    const union = new Set(activeMidis);
    pickedMidis.forEach((m) => union.add(m));
    return union;
  }, [surface, activeMidis, pickedMidis]);

  const handleRangeChange = useCallback((nextLow, nextHigh) => {
    const clamped = clampRange(nextLow, nextHigh);
    setLow(clamped.low);
    setHigh(clamped.high);
  }, []);

  const pocket = surface === "pocket";
  const pocketChordName = pocketDegrees.length
    ? chordLabel(keyName, pocketDegrees[pocketIdx] || pocketDegrees[0])
    : null;
  const pocketNextName = pocketDegrees.length
    ? chordLabel(keyName, pocketDegrees[(pocketIdx + 1) % pocketDegrees.length])
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-rose-50 text-slate-800">
      <header className="mx-auto flex max-w-5xl flex-wrap items-end justify-between gap-3 px-4 pb-2 pt-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Piano Drills <span aria-hidden>🎹</span>
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Build a chord progression, pick a rhythm, and drill it — guided or graded.
          </p>
          <div className="mt-3 flex w-fit overflow-hidden rounded-lg border border-slate-200 bg-white/80">
            {SURFACES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSurface(s.id)}
                aria-pressed={surface === s.id}
                className={cn(
                  "px-4 py-1.5 text-sm transition-colors",
                  surface === s.id
                    ? "bg-primary font-medium text-primary-foreground"
                    : "text-slate-600 hover:bg-accent"
                )}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-4">
          <Link
            to="/circle-of-fifths"
            className="text-sm text-indigo-600 underline-offset-2 hover:underline"
          >
            circle of fifths →
          </Link>
          <Link
            to="/chord-caller"
            className="text-sm text-indigo-600 underline-offset-2 hover:underline"
          >
            chord caller →
          </Link>
          <Link
            to="/sillyprojects"
            className="text-sm text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline"
          >
            ← silly projects
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-4 px-4 pb-16 pt-4">
        <DrillControls
          keyName={keyName}
          onKeyChange={setKeyName}
          progression={progression}
          onProgressionChange={setProgression}
          progressionText={progressionText}
          onProgressionTextChange={setProgressionText}
          progressionError={typedProg ? typedProg.error : null}
          rhythmId={rhythmId}
          onRhythmChange={setRhythmId}
          patternText={patternText}
          onPatternTextChange={setPatternText}
          patternError={parsedPattern ? parsedPattern.error : null}
          voicingId={voicingId}
          onVoicingChange={setVoicingId}
          inversionModeId={inversionModeId}
          onInversionModeChange={setInversionModeId}
          bassId={bassId}
          onBassChange={setBassId}
          low={low}
          high={high}
          onRangeChange={handleRangeChange}
          bpm={bpm}
          onBpmChange={setBpm}
          mode={mode}
          onModeChange={setMode}
          metronome={metronome}
          onMetronomeChange={setMetronome}
          playing={playing}
          onTogglePlay={toggle}
          midiStatus={midiStatus}
          midiInputs={midiInputs}
          drillError={pocket ? null : drill.error}
          pocketMode={pocket}
        />

        {!pocket && mode === "score" && <ScoreBar score={score} playing={playing} />}

        {!pocket && (
          <SheetView
            drill={drill}
            currentEventIndices={currentEventIndices}
            statuses={mode === "score" ? statuses : {}}
          />
        )}

        {pocket && (
          <div className="rounded-xl border border-slate-200 bg-white/80 px-4 py-3">
            {!pocketDegrees.length ? (
              <p className="text-sm text-slate-500">
                No chords to drill — click some above, or type a progression.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                      Play this chord — any voicing you like, inside the lit window
                    </p>
                    <p className="mt-1 text-3xl font-semibold text-slate-900">
                      {pocketChordName}
                    </p>
                  </div>
                  <p className="text-sm text-slate-500">
                    next: <span className="font-medium text-slate-700">{pocketNextName}</span>
                  </p>
                  <span className="ml-auto text-sm font-medium text-slate-600">
                    Lap {pocketLap} · chord {pocketIdx + 1}/{pocketDegrees.length}
                  </span>
                  <button
                    type="button"
                    onClick={resetPocket}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                  >
                    Reset laps
                  </button>
                </div>
                {pocketStatus && pocketStatus.type === "ok" ? (
                  <p className="mt-2 text-sm font-medium text-emerald-600">
                    ✓ That's {pocketStatus.label}.
                    {pocketStatus.coach && (
                      <span className="font-normal text-emerald-700"> {pocketStatus.coach}.</span>
                    )}
                  </p>
                ) : pocketStatus && pocketStatus.type === "window" ? (
                  <p className="mt-2 text-sm font-medium text-rose-600">
                    Right chord — keep it inside the lit window.
                  </p>
                ) : pocketStatus && pocketStatus.type === "wrong" ? (
                  <p className="mt-2 text-sm font-medium text-rose-600">
                    Hmm — that's not {pocketChordName}. Adjust and try again.
                  </p>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">
                    Tap or hold at least three keys — any inversion counts while every note
                    stays between the amber lines.
                  </p>
                )}
                {pickedMidis.size > 0 && (!pocketStatus || pocketStatus.type !== "ok") && (
                  <button
                    type="button"
                    onClick={() => setPickedMidis(EMPTY_SET)}
                    className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm hover:border-indigo-300"
                  >
                    Clear notes
                  </button>
                )}
              </>
            )}
          </div>
        )}

        <PianoRoll
          low={low}
          high={high}
          activeMidis={rollActiveMidis}
          targetMidis={pocket ? EMPTY_SET : targetMidis}
          onPress={pocket ? pocketTap : press}
          onRelease={pocket ? undefined : release}
          kbHints={kbHints}
          useFlats={Boolean(drill.useFlats)}
          window={pocket ? POCKET_WINDOW : null}
        />

        <p className="text-center text-xs text-slate-400">
          Plug in a MIDI keyboard (it's picked up automatically),{" "}
          {pocket ? "tap keys to build a chord" : "click the keys"}, or use the A–K row on
          your computer keyboard.
        </p>
      </main>
    </div>
  );
}
